/* eslint-disable functional/prefer-readonly-type */
/* eslint-disable id-length */
/* eslint-disable max-lines-per-function */
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import React, { useEffect } from 'react'
import Layout from '~/components/Layout'
import { useLang } from '~/hooks/useLang'
import InternalHeader from '~/components/Layout/components/InternalHeader'
import InternalContent from '~/components/Layout/components/InternalContent'
import StationCardMonitor from '~/features/Entry/components/StationCardMonitor'
import { Responsive, WidthProvider as widthProvider } from 'react-grid-layout'
import {
  deleteTag,
  getManagentStationsEntryService,
  getManagentStationsTagService,
  saveStationsManagementTags,
  saveStationsPositions,
} from '~/features/Entry/services'
import { Downgraded, useHookstate } from '@hookstate/core'
import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material'
import notify from '~/utils/notify'
import ConfirmDialog from '~/components/ConfirmDialog'
import CreateTag from '../components/CreateTag'
import StationCardDivisor from '../components/StationCardDivisor'
import { IDivisors } from '../interfaces/IDivisors'
import AddIcon from '@mui/icons-material/Add'

const ResponsiveGridLayout = widthProvider(Responsive)

interface ILayout {
  readonly i: string
  readonly h: number
  readonly w: number
  readonly x: number
  readonly y: number
}

export default function ManagementStations() {
  const { translate } = useLang()
  const divisors = useHookstate<readonly IDivisors[]>([])
  const divisorsListOpen = useHookstate(false)
  const divisorName = useHookstate('')
  const forceRefresh = useHookstate(false)
  const openDialog = useHookstate(false)
  const loadingDeleteButton = useHookstate(false)
  const removedTag = useHookstate<IDivisors>({
    name: '',
    position_x: 0,
    position_y: 0,
    width: 0,
    height: 0,
  })
  const tagIndex = useHookstate<number | null>(null)
  const newDivisors = useHookstate<readonly IDivisors[]>([])
  const data = useHookstate<any[]>([])
  const unpositionedData = useHookstate<any[]>([])
  const loading = useHookstate(false)

  const loadData = async () => {
    loading.set(true)

    try {
      const [station, tag] = await Promise.all([
        getManagentStationsEntryService(),
        getManagentStationsTagService(),
      ])

      const allData = [...station, ...tag]

      const positioned = allData.filter((item) => {
        const x = item.station?.position_x ?? item.position_x
        const y = item.station?.position_y ?? item.position_y
        // Considera posicionado se x ou y forem diferentes de null/undefined
        // Apenas considera "sem posição" se ambos forem exatamente null ou undefined
        return x !== null && x !== undefined && y !== null && y !== undefined
      })

      const unpositioned = allData.filter((item) => {
        const x = item.station?.position_x ?? item.position_x
        const y = item.station?.position_y ?? item.position_y
        // Sem posição apenas se x ou y forem null/undefined
        return x === null || x === undefined || y === null || y === undefined
      })

      data.set(
        [...positioned].sort((a, b) => {
          const aX = Number(a.station?.position_x ?? a.position_x)
          const bX = Number(b.station?.position_x ?? b.position_x)
          const aY = Number(a.station?.position_y ?? a.position_y)
          const bY = Number(b.station?.position_y ?? b.position_y)

          if (aX !== bX) return bX - aX
          if (aY !== bY) return bY - aY
          return 0
        })
      )

      unpositionedData.set(unpositioned)
      divisors.set(tag)
    } catch (error) {
      notify({
        message: translate('Failed to load data'),
        type: 'error',
      })
    } finally {
      loading.set(false)
    }
  }

  // CORRIGIDO: usar Downgraded para evitar recursão
  useEffect(() => {
    loadData()
  }, [forceRefresh.attach(Downgraded).get()])

  const handleChangeLayout = (newLayout: readonly ILayout[]) => {
    // CORRIGIDO: usar Downgraded
    const currentData = data.attach(Downgraded).get()
    const currentNewDivisors = newDivisors.attach(Downgraded).get()

    const updatedData = currentData.map((item, idx) => {
      if (!item) return item

      const stationKey = item.station
        ? `station-${item.station.id ?? idx}`
        : `divisor-${item.id ?? item.name ?? idx}`

      const match = newLayout.find((layout) => layout.i === stationKey)

      if (match && item.station) {
        return {
          ...item,
          station: {
            ...item.station,
            position_x: Number(match.x),
            position_y: Number(match.y),
            width: Number(match.w),
            height: Number(match.h),
          },
        }
      }

      if (match && !item.station) {
        return {
          ...item,
          position_x: Number(match.x),
          position_y: Number(match.y),
          width: Number(match.w),
          height: Number(match.h),
        }
      }

      return item
    })

    const updatedNewDivisors = currentNewDivisors.map((divisor, idx) => {
      if (!divisor) return divisor

      const divisorKey = `newdivisor-${divisor.id ?? divisor.name ?? idx}`
      const match = newLayout.find((layout) => layout.i === divisorKey)

      if (match) {
        return {
          ...divisor,
          position_x: Number(match.x),
          position_y: Number(match.y),
          width: Number(match.w),
          height: Number(match.h),
        }
      }

      return divisor
    })

    data.set(updatedData)
    newDivisors.set(updatedNewDivisors)
  }

  const handleSaveChange = async () => {
    loading.set(true)
    // CORRIGIDO: usar Downgraded
    const currentData = data.attach(Downgraded).get()

    const newStationsPositions = currentData
      .filter((item) => item.station)
      .map((item) => item.station)

    const newTagsPositions = currentData.filter((item) => !item.station)

    try {
      await saveStationsPositions(newStationsPositions)
      notify({
        message: translate('Stations position saved successfully!'),
        type: 'success',
      })

      await saveStationsManagementTags(newTagsPositions)
      notify({
        message: translate('Tags position saved successfully!'),
        type: 'success',
      })
    } catch {
      notify({
        message: translate('Something went wrong! Try again'),
        type: 'error',
      })
    } finally {
      newDivisors.set([])
      loadData()
    }
  }

  const handleAgreeDeleteTag = (olderTag) => {
    try {
      if (olderTag.id) {
        deleteTag(removedTag.attach(Downgraded).get().id).finally(() =>
          forceRefresh.set((prev) => !prev)
        )
      } else {
        const newTags = newDivisors
          .attach(Downgraded)
          .get()
          .filter((tag) => tag !== olderTag)
        newDivisors.set(newTags)
      }
      notify({
        message: translate('Tag delete successfully!'),
        type: 'success',
      })
    } catch {
      notify({
        message: translate('Something went wrong! Try again'),
        type: 'error',
      })
    }

    openDialog.set(false)
  }

  const handleAddToGrid = (item: any, index: number) => {
    // CORRIGIDO: usar Downgraded
    const currentData = data.attach(Downgraded).get()
    const existingPositions = currentData.map((d) => ({
      x: Number(d.station?.position_x ?? d.position_x),
      y: Number(d.station?.position_y ?? d.position_y),
    }))

    let newX = 0
    let newY = 0
    let positionFound = false

    for (let y = 0; y < 100 && !positionFound; y++) {
      for (let x = 0; x < 12 && !positionFound; x++) {
        const isOccupied = existingPositions.some(
          (pos) => pos.x === x && pos.y === y
        )
        if (!isOccupied) {
          newX = x
          newY = y
          positionFound = true
        }
      }
    }

    const updatedItem = item.station
      ? {
          ...item,
          station: {
            ...item.station,
            position_x: newX,
            position_y: newY,
            width: item.station.width || 2,
            height: item.station.height || 1,
          },
        }
      : {
          ...item,
          position_x: newX,
          position_y: newY,
          width: item.width || 6,
          height: item.height || 0.3,
        }

    // CORRIGIDO: usar Downgraded
    const currentUnpositioned = unpositionedData.attach(Downgraded).get()
    const newUnpositioned = currentUnpositioned.filter((_, i) => i !== index)

    unpositionedData.set(newUnpositioned)
    data.set([...currentData, updatedItem])

    notify({
      message: translate('Item added to grid'),
      type: 'success',
    })
  }

  return (
    <Layout marginLayout={true}>
      <InternalHeader
        title={translate(`Management Stations`)}
        action={
          <Grid sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => divisorsListOpen.set(true)}
            >
              {translate('Tags Management')}
            </Button>
            <Button variant="contained" onClick={handleSaveChange}>
              {translate('Save')}
            </Button>
          </Grid>
        }
      />

      <InternalContent title={translate('Filters')} expandButton={false}>
        {loading.get() ? (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{ height: '200px' }}
          >
            <CircularProgress />
          </Grid>
        ) : (
          <>
            {unpositionedData.get().length > 0 && (
              <Paper
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                }}
              >
                <Typography 
                  variant="subtitle1" 
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: '#555',
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {translate('Items without position')}
                  <Typography
                    component="span"
                    sx={{
                      backgroundColor: '#666',
                      color: '#fff',
                      borderRadius: '10px',
                      px: 1,
                      py: 0.25,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      minWidth: '24px',
                      textAlign: 'center',
                    }}
                  >
                    {unpositionedData.get().length}
                  </Typography>
                </Typography>
                <List sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 1,
                  p: 0,
                }}>
                  {unpositionedData.get().map((item, index) => {
                    if (!item) return null

                    const itemKey = item.station?.id
                      ? `unpositioned-station-${item.station.id}`
                      : `unpositioned-tag-${item.id ?? item.name ?? index}`

                    return (
                      <ListItem
                        key={itemKey}
                        sx={{
                          backgroundColor: '#fff',
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#bdbdbd',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          },
                          px: 2,
                          py: 1,
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="add"
                            size="small"
                            onClick={() => handleAddToGrid(item, index)}
                            sx={{
                              backgroundColor: 'rgb(3, 140, 127)',
                              color: '#fff',
                              '&:hover': {
                                backgroundColor: 'rgb(2, 120, 108)',
                              },
                              width: 32,
                              height: 32,
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={
                            <Typography
                              sx={{
                                fontWeight: 500,
                                color: '#333',
                                fontSize: '0.875rem',
                              }}
                            >
                              {item.station?.name ??
                                item.name ??
                                translate('Unnamed item')}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              component="span"
                              sx={{
                                color: item.station ? '#1976d2' : '#f57c00',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                              }}
                            >
                              {item.station
                                ? translate('Station')
                                : translate('Tag')}
                            </Typography>
                          }
                        />
                      </ListItem>
                    )
                  })}
                </List>
              </Paper>
            )}
            <div
              style={{
                position: 'relative',
                height: '900px',
                overflow: 'auto',
              }}
            >
              <ResponsiveGridLayout
                className="layout"
                compactType="vertical"
                autoSize={false}
                allowOverlap={false}
                preventCollision={false}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                onLayoutChange={handleChangeLayout}
              >
                {data.get().map((item, index) => {
                  if (!item) return null

                  if (item.station) {
                    return (
                      <div
                        key={`station-${item.station.id ?? index}`}
                        data-grid={{
                          x: Number(item.station.position_x),
                          y: Number(item.station.position_y),
                          w: Number(item.station.width),
                          h: Number(item.station.height),
                          minW: 2,
                          minH: 1,
                          maxH: 1,
                        }}
                      >
                        <StationCardMonitor
                          station={item.station}
                          user={item.user}
                          stationIsOnline={item.station_is_online}
                        />
                      </div>
                    )
                  } else {
                    return (
                      <div
                        key={`divisor-${item.id ?? item.name ?? index}`}
                        data-grid={{
                          x: Number(item.position_x),
                          y: Number(item.position_y),
                          w: Number(item.width),
                          h: Number(item.height),
                          minH: 0.3,
                          maxH: 0.3,
                        }}
                      >
                        <StationCardDivisor divisorName={item.name} />
                      </div>
                    )
                  }
                })}
                {newDivisors.get().map((divisor, index) => {
                  if (!divisor) return null

                  return (
                    <div
                      key={`newdivisor-${divisor.id ?? divisor.name ?? index}`}
                      data-grid={{
                        x: Number(divisor.position_x),
                        y: Number(divisor.position_y),
                        w: Number(divisor.width),
                        h: Number(divisor.height),
                        minH: 0.3,
                        maxH: 0.3,
                      }}
                    >
                      <StationCardDivisor divisorName={divisor.name} />
                    </div>
                  )
                })}
              </ResponsiveGridLayout>
            </div>
          </>
        )}
      </InternalContent>

      <CreateTag
        divisorsListOpen={divisorsListOpen}
        divisorName={divisorName}
        openDialog={openDialog}
        removedTag={removedTag}
        tagIndex={tagIndex}
        newDivisors={newDivisors}
        handleSaveChange={handleSaveChange}
        data={data}
        divisors={divisors}
      />

      <ConfirmDialog
        handleAgree={() =>
          handleAgreeDeleteTag(removedTag.attach(Downgraded).get())
        }
        handleClose={() => openDialog.set(false)}
        open={openDialog}
        title={translate('Confirmation')}
        content={`${translate('Confirm the delete of the tag')} "${
          removedTag.attach(Downgraded).get().name
        }"?`}
        loading={loadingDeleteButton}
        AgreeLabel={translate('Ok')}
      />
    </Layout>
  )
}
